/**
 * "Building your Construct" (plan §23.3, API-CONTRACT §2 / Appendix B).
 *
 * Polls GET /api/deployment/status every 2s while QUEUED / PROVISIONING / VERIFYING and stops
 * at READY or a *_FAILED state.
 *
 * §23.3 is strict about language: stable, human, and never a provider error. The API already
 * ships human step labels; the thirteen saga steps are collapsed into the five phrases the plan
 * names, because "create_r2_credential" is not a sentence anyone should have to read.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../api/client.js";
import { Banner, ErrorNote, Loading } from "../components/ui.js";
import { useSession } from "../state/session.js";
import type { DeploymentStatusResponse, ProvisioningStep, StepState } from "../api/types.js";

const POLL_MS = 2000;

/** §23.3's five customer-facing phases, and which saga steps roll up into each. */
const PHASES: { label: string; keys: string[] }[] = [
  { label: "Creating your private vault", keys: ["validate_entitlement", "reserve_names", "create_git_vault", "create_deploy_key"] },
  { label: "Preparing secure storage", keys: ["create_r2_bucket", "create_r2_credential"] },
  { label: "Preparing search and recall", keys: ["create_db_tenant", "apply_platform_migrations"] },
  { label: "Starting your Construct", keys: ["create_secrets", "mint_bootstrap_principals", "deploy_cloud_run", "create_sweep_trigger"] },
  { label: "Verifying everything", keys: ["verify_health"] },
];

/** A phase is only as far along as its least-finished step. */
function phaseState(steps: ProvisioningStep[], keys: string[]): StepState {
  const mine = steps.filter((s) => keys.includes(s.key));
  if (!mine.length) return "PENDING";
  if (mine.some((s) => s.state === "FAILED")) return "FAILED";
  if (mine.some((s) => s.state === "RUNNING")) return "RUNNING";
  if (mine.every((s) => s.state === "SUCCEEDED" || s.state === "SKIPPED")) return "SUCCEEDED";
  return "PENDING";
}

const POLLING_STATES = new Set(["QUEUED", "PROVISIONING", "VERIFYING"]);
const FAILED_STATES = new Set(["PROVISIONING_FAILED", "VERIFYING_FAILED"]);

export default function Building() {
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const [status, setStatus] = useState<DeploymentStatusResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const timer = useRef<number | null>(null);

  const poll = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await api.deploymentStatus(signal);
      setStatus(next);
      setError(null);
      return next;
    } catch (e) {
      if ((e as Error).name === "AbortError") return null;
      setError(e);
      return null;
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    const tick = async () => {
      const next = await poll(ac.signal);
      if (cancelled) return;
      if (next?.status === "READY") {
        await refresh();
        navigate("/dashboard", { replace: true });
        return;
      }
      if (next && !POLLING_STATES.has(next.status ?? "")) return; // terminal — stop polling
      timer.current = window.setTimeout(() => void tick(), POLL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      ac.abort();
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [poll, refresh, navigate]);

  if (!me) return null;
  if (!me.deployment && status?.status == null) {
    // Nothing has been ordered yet.
    return <Navigate to="/plans" replace />;
  }
  if (!status) return <Loading label="Checking on your Construct" />;

  const steps = status.provisioning?.steps ?? [];
  const phases = PHASES.map((p) => ({ ...p, state: phaseState(steps, p.keys) }));
  const done = phases.filter((p) => p.state === "SUCCEEDED").length;
  const percent = Math.round((done / phases.length) * 100);
  const failed = FAILED_STATES.has(status.status ?? "");
  const requestId = status.provisioning?.run?.id ?? null;

  return (
    <div className="wrap--narrow">
      <div className="page-head">
        <p className="eyebrow">Step 2 of 2</p>
        <h1>{failed ? "Something went wrong" : "Building your Construct"}</h1>
        <p className="lede">
          {failed
            ? "We hit a problem setting things up. Nothing has been lost, and no further charge has been made. Our team can pick this up straight away."
            : "This usually takes a couple of minutes. You can close this tab; we will keep going, and it will be waiting for you."}
        </p>
      </div>

      {error ? <ErrorNote error={error} onRetry={() => void poll()} /> : null}

      {!failed ? (
        <div className="bar" aria-hidden="true">
          <span style={{ width: `${percent}%` }} />
        </div>
      ) : null}

      <div className="card">
        <ul className="steps">
          {phases.map((p) => (
            <li key={p.label} data-state={p.state}>
              <span className="steps__dot" data-state={p.state} aria-hidden="true" />
              <span className="steps__label">{p.label}</span>
              <span className="mono small muted">
                {p.state === "SUCCEEDED" ? "done" : p.state === "RUNNING" ? "working" : p.state === "FAILED" ? "stopped" : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {failed ? (
        <div style={{ marginTop: 20 }}>
          <Banner kind="error" title="We are on it">
            Get in touch and quote the reference below and we will sort it out.
            {requestId ? <p className="mono small muted" style={{ marginTop: 8 }}>Reference {requestId}</p> : null}
          </Banner>
          <div className="btn-row">
            <a className="btn btn--primary" href="mailto:support@theconstruct.site">
              Contact support
            </a>
            <button type="button" className="btn btn--ghost" onClick={() => void poll()}>
              Check again
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
